import { useCallback, useEffect, useMemo, useState } from 'react'

import { toaster } from '@/system/toaster'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Box,
  Button,
  Center,
  CloseButton,
  Dialog,
  Grid,
  HStack,
  Icon,
  Input,
  Portal,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { RequiredFieldMark } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import {
  CreateInspectionAreaWithFilesParams,
  FileProcessingStatus,
  InspectionArea,
  Project,
  UpdateInspectionAreaWithFilesParams,
  getErrorMesssage,
  getShapes,
} from '@infraloop/services'
import { LuBoxes, LuPencil } from 'react-icons/lu'

import useInspectionSheet from '../../hooks/useInspectionSheet'
import FileDrop from './FileDrop'
import FormField from './FormField'
import SectionHeading from './SectionHeading'

const AreaFormModal = ({
  project,
  areas,
  area,
  isOpen,
  onClose,
  onCreate,
  onUpdate,
}: {
  /** Project the area belongs to (name + construction defaults). */
  project: Project
  /** The project's existing areas, for the duplicate-name check. */
  areas: InspectionArea[]
  /** Area to edit; omit to create a new one. */
  area?: InspectionArea
  /** Whether the modal is open. */
  isOpen: boolean
  /** Called when the modal requests to close. */
  onClose: () => void
  /** Creates the area and uploads its files (the areas hook's mutation). */
  onCreate: (params: CreateInspectionAreaWithFilesParams) => Promise<InspectionArea>
  /** Updates the area, its sheet, and uploads replacement files (the areas hook's mutation). */
  onUpdate: (params: UpdateInspectionAreaWithFilesParams) => Promise<InspectionArea>
}) => {
  // Hooks
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()

  // Derived state
  const isEdit = Boolean(area)
  // A point cloud can't be replaced once uploaded — its downsample and any shapes detected on
  // it derive from that file — so the PCD field is read-only in edit mode (shows the filename,
  // no upload), unless the previous upload failed. Mesh stays replaceable. Matches legacy Hatsuly.
  const isPcdReadOnly =
    isEdit && Boolean(area?.origin_file?.name) && area?.downsample_status?.status !== FileProcessingStatus.Failed

  // Data — the area's inspection sheet backs the metadata fields when editing.
  const { sheet, isLoadingSheet } = useInspectionSheet(
    project.project_id,
    area?.inspection_area_id ?? '',
    isOpen && isEdit,
  )

  // States
  const [areaName, setAreaName] = useState('')
  const [pcdFile, setPcdFile] = useState<File | null>(null)
  const [meshFile, setMeshFile] = useState<File | null>(null)
  const [workType, setWorkType] = useState('')
  const [areaType, setAreaType] = useState('')
  const [creatorName, setCreatorName] = useState('')
  const [createdDate, setCreatedDate] = useState('')
  const [observerName, setObserverName] = useState('')
  const [observedDate, setObservedDate] = useState('')
  const [isSheetSeeded, setIsSheetSeeded] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Derived state — the check goes quiet while submitting: a successful create adds the
  // new area to the list before this modal unmounts (and it stays on screen through the
  // close animation), which would otherwise flag the name the user just saved as a
  // duplicate of its own row.
  const isNameDuplicate = useMemo(() => {
    if (isUploading) return false
    const name = areaName.trim().toLowerCase()
    if (!name) return false
    return areas.some(
      (a) => a.inspection_area_name.toLowerCase() === name && a.inspection_area_id !== area?.inspection_area_id,
    )
  }, [areas, areaName, area, isUploading])
  const isFormValid = Boolean(areaName.trim()) && !isNameDuplicate
  const isFormReady = !isEdit || !isLoadingSheet

  /**
   * Reset the form whenever the modal (re)opens — the name comes from the area when
   * editing; 工種名/種別 seed from the project's defaults when creating (the sheet
   * seeds them for edits, once it loads).
   */
  useEffect(() => {
    if (!isOpen) return
    setAreaName(area?.inspection_area_name ?? '')
    setPcdFile(null)
    setMeshFile(null)
    setWorkType(area ? '' : (project.default_construction_properties?.construction_type ?? ''))
    setAreaType(area ? '' : (project.default_construction_properties?.construction_type_detailed ?? ''))
    setCreatorName('')
    setCreatedDate('')
    setObserverName('')
    setObservedDate('')
    setIsSheetSeeded(false)
    setUploadProgress(0)
  }, [isOpen, project, area])

  /**
   * Seed the metadata fields from the area's inspection sheet once it loads (edit mode
   * only, once per open so a background refetch can't clobber the user's edits).
   */
  useEffect(() => {
    if (!isOpen || !isEdit || isLoadingSheet || isSheetSeeded) return
    setWorkType(sheet?.construction_properties?.construction_type ?? '')
    setAreaType(sheet?.construction_properties?.construction_type_detailed ?? '')
    setCreatorName(sheet?.creator_name ?? '')
    setCreatedDate(sheet?.create_time_user_specified?.slice(0, 10) ?? '')
    setObserverName(sheet?.observer_name ?? '')
    setObservedDate(sheet?.observe_time_user_specified?.slice(0, 10) ?? '')
    setIsSheetSeeded(true)
  }, [isOpen, isEdit, isLoadingSheet, isSheetSeeded, sheet])

  /**
   * Accept the picked point-cloud file, seeding the area name from it when empty.
   * @param file Picked point-cloud file.
   */
  const handlePcdSelect = useCallback(
    (file: File) => {
      setPcdFile(file)
      if (!areaName) setAreaName(file.name.replace(/\.(las|laz|ply)$/i, ''))
    },
    [areaName],
  )

  /**
   * Create or update the area (files optional), then close. Replacing an existing point
   * cloud is blocked while detected shapes exist on the area — they derive from the old
   * point cloud, matching the legacy dashboard's guard. On failure the mutation has
   * toasted the error, so the modal stays open.
   */
  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return

    setIsUploading(true)
    setUploadProgress(0)

    const constructionProperties = {
      ...(isEdit ? sheet?.construction_properties : project.default_construction_properties),
      construction_type: workType.trim(),
      construction_type_detailed: areaType.trim(),
    }
    // Date-only strings parse as UTC midnight, matching the legacy dashboard's values.
    const createTime = createdDate ? new Date(createdDate).toISOString() : null
    const observeTime = observedDate ? new Date(observedDate).toISOString() : null

    try {
      if (area) {
        // Replacing the point cloud would orphan shapes detected on the previous one.
        if (pcdFile && area.down_sampled_file?.name) {
          try {
            const accessToken = await getAccessTokenSilently()
            const shapes = await getShapes(accessToken, project.project_id, area.inspection_area_id)
            if (shapes.cylinders.length || shapes.polygons.length) {
              toaster.error({ description: t('hatsuly.modal.area.edit.shapesExistError') })
              return
            }
          } catch (error) {
            toaster.error({ description: getErrorMesssage(error, t) })
            return
          }
        }

        await onUpdate({
          inspectionAreaId: area.inspection_area_id,
          inspectionArea: { inspection_area_name: areaName.trim() },
          // Spread the fetched sheet so fields beyond the editable ones (e.g. the
          // criterion values the endpoint requires) are echoed back.
          sheet: sheet
            ? {
                ...sheet,
                construction_properties: constructionProperties,
                creator_name: creatorName.trim(),
                create_time_user_specified: createTime,
                observer_name: observerName.trim(),
                observe_time_user_specified: observeTime,
              }
            : undefined,
          pcdFile,
          meshFile,
          onProgress: setUploadProgress,
        })
      } else {
        await onCreate({
          inspectionArea: {
            inspection_area_name: areaName.trim(),
            creator_name: creatorName.trim() || undefined,
            create_time_user_specified: createTime ?? undefined,
            observer_name: observerName.trim() || undefined,
            observe_time_user_specified: observeTime ?? undefined,
            construction_properties: constructionProperties,
          },
          pcdFile,
          meshFile,
          onProgress: setUploadProgress,
        })
      }
      onClose()
    } catch {
      // Error feedback is handled by the mutation's onError toast.
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }, [
    isFormValid,
    isEdit,
    area,
    sheet,
    pcdFile,
    meshFile,
    areaName,
    workType,
    areaType,
    creatorName,
    createdDate,
    observerName,
    observedDate,
    project,
    getAccessTokenSilently,
    onCreate,
    onUpdate,
    onClose,
    t,
  ])

  /**
   * Close unless an upload is in flight.
   */
  const handleClose = useCallback(() => {
    if (!isUploading) onClose()
  }, [isUploading, onClose])

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && handleClose()}
      size="xl"
      placement="center"
      closeOnEscape={!isUploading}
      closeOnInteractOutside={!isUploading}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="16px" maxW="720px">
            <Dialog.Header borderBottom="1px solid" borderColor="gray.100" gap="12px" alignItems="flex-start">
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="38px"
                h="38px"
                borderRadius="10px"
                bg="primary.50"
                color="primary.600"
                flexShrink={0}
              >
                <Icon as={isEdit ? LuPencil : LuBoxes} boxSize="19px" />
              </Box>
              <VStack flex="1" align="start" gap="4px" minW={0}>
                <Dialog.Title fontSize="19px" fontWeight="bold" color="gray.800">
                  {isEdit ? t('hatsuly.modal.area.edit.title') : t('hatsuly.modal.area.add.title')}
                </Dialog.Title>
                <HStack gap="7px" fontSize="12px" color="gray.400" fontWeight="medium">
                  <Text as="span">{t('hatsuly.modal.area.add.projectLabel')}</Text>
                  <Text as="span" color="gray.700" fontWeight="semibold" lineClamp={1}>
                    {project.project_name}
                  </Text>
                </HStack>
              </VStack>
              <Dialog.CloseTrigger asChild disabled={isUploading}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py="24px">
              {!isFormReady && (
                <Center py="60px">
                  <Spinner size="sm" color="primary.500" />
                </Center>
              )}

              {isFormReady && (
                <>
                  <Box>
                    <Text as="label" display="block" fontSize="13px" fontWeight="semibold" color="gray.700" mb="7px">
                      {t('hatsuly.modal.area.add.nameLabel')} <RequiredFieldMark />
                    </Text>
                    <Input
                      value={areaName}
                      onChange={(e) => setAreaName(e.target.value)}
                      disabled={isUploading}
                      borderRadius="9px"
                      bg="white"
                      data-testid="create-area-name"
                    />
                    {isNameDuplicate && (
                      <Text fontSize="12px" color="red.600" mt="6px">
                        {t('modal.inspectionArea.add.areaNameDuplicateError')}
                      </Text>
                    )}
                  </Box>

                  <SectionHeading label={t('hatsuly.modal.area.add.dataSection')} />
                  <Grid templateColumns="1fr 1fr" gap="14px">
                    <FileDrop
                      label={t('hatsuly.modal.area.add.pointCloud')}
                      formats={t('hatsuly.modal.area.add.pointCloudFormats')}
                      prompt={t('hatsuly.modal.area.add.chooseFile')}
                      orDrop={t('hatsuly.modal.area.add.orDrop')}
                      accept=".las,.laz,.ply"
                      file={pcdFile}
                      existingFileName={area?.origin_file?.name}
                      onSelect={handlePcdSelect}
                      disabled={isUploading}
                      readOnly={isPcdReadOnly}
                    />
                    <FileDrop
                      label={t('hatsuly.modal.area.add.mesh')}
                      formats={t('hatsuly.modal.area.add.meshFormats')}
                      prompt={t('hatsuly.modal.area.add.chooseFile')}
                      orDrop={t('hatsuly.modal.area.add.orDrop')}
                      accept=".zip"
                      file={meshFile}
                      existingFileName={area?.mesh_bundle_file?.name}
                      onSelect={setMeshFile}
                      disabled={isUploading}
                    />
                  </Grid>

                  <SectionHeading
                    label={t('hatsuly.modal.area.add.inspectionSection')}
                    note={t('hatsuly.modal.area.add.optional')}
                  />
                  <Grid templateColumns="1fr 1fr" gap="14px 18px">
                    <FormField
                      label={t('hatsuly.modal.area.add.workTypeLabel')}
                      value={workType}
                      onChange={setWorkType}
                      disabled={isUploading}
                    />
                    <FormField
                      label={t('hatsuly.modal.area.add.typeLabel')}
                      value={areaType}
                      onChange={setAreaType}
                      disabled={isUploading}
                    />
                    <FormField
                      label={t('hatsuly.modal.area.add.authorLabel')}
                      value={creatorName}
                      onChange={setCreatorName}
                      disabled={isUploading}
                    />
                    <FormField
                      label={t('hatsuly.modal.area.add.createdDateLabel')}
                      type="date"
                      value={createdDate}
                      onChange={setCreatedDate}
                      disabled={isUploading}
                    />
                    <FormField
                      label={t('hatsuly.modal.area.add.witnessLabel')}
                      value={observerName}
                      onChange={setObserverName}
                      disabled={isUploading}
                    />
                    <FormField
                      label={t('hatsuly.modal.area.add.witnessDateLabel')}
                      type="date"
                      value={observedDate}
                      onChange={setObservedDate}
                      disabled={isUploading}
                    />
                  </Grid>
                </>
              )}
            </Dialog.Body>

            <Dialog.Footer borderTop="1px solid" borderColor="gray.100" bg="#FBFCFE" gap="10px">
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                {t('ui.cancel')}
              </Button>
              <Button
                colorPalette="blue"
                disabled={!isFormValid || !isFormReady}
                loading={isUploading}
                loadingText={uploadProgress > 0 ? t('ui.uploading', { progress: uploadProgress }) : undefined}
                onClick={handleSubmit}
                data-testid="create-area-submit"
              >
                {isEdit ? t('ui.save') : t('ui.create')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default AreaFormModal
